import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Image } from 'lucide-react';
import CustomDropdown from '@/components/common-components/filter options/CustomDropdown';
import CreativeUpload from '@/components/common-components/uploads/Creative';
import LiveAdPreview from '@/components/common-components/dialogBoxes/LiveAdPreview';
import { useCampaignAPI } from '@/hooks/campaigns/useCampaignAPI';


const CreativeUploadForm = forwardRef(({
  creativeData,
  onCreativeDataChange,
  campaignName
}, ref) => {
  const { campaignOptions, loading: optionsLoading, getCampaignOptions } = useCampaignAPI();

  const [formData, setFormData] = useState({
    creatives: [],
    creative_type: 'existing',
    creative_description: '',
    existing_creative_description: '',
    new_creative_description: '',
    ad_headline: '',
    body_copy: '',
    min_age: '',
    max_age: '',
    cta_type: '',
    additional_comment: '',
    ...creativeData
  });

  // Fetch campaign options on component mount
  useEffect(() => {
    getCampaignOptions();
  }, [getCampaignOptions]);

  // Update form data when creativeData prop changes
  useEffect(() => {
    if (creativeData) {
      const cleanCreativeData = { ...creativeData };
      delete cleanCreativeData.creatives;

      setFormData(prev => ({
        ...prev,
        ...cleanCreativeData,
        // Only reset creatives if they're not already set in the current state
        creatives: prev.creatives.length > 0 ? prev.creatives : []
      }));
    }
  }, [creativeData]);

  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    if (field !== 'creatives') {
      onCreativeDataChange(newData);
    }
  };

  const handleFilesChange = (files) => {
    handleInputChange('creatives', files);
  };

  const handleCreativeTypeChange = (value) => {
    const newData = {
      ...formData,
      creative_type: value,
      creatives: [],
      creative_description: '',
      existing_creative_description: '',
      new_creative_description: ''
    };
    setFormData(newData);
    onCreativeDataChange(newData);
  };

  const validateForm = () => {
    if (!formData.ad_headline || !formData.body_copy) {
      alert('Please fill in the required fields: Ad Headline and Body Copy');
      return false;
    }

    if (!formData.creative_type) {
      alert('Please select a creative source');
      return false;
    }

    if (formData.creative_type === 'upload') {
      // Check if at least one of the fields is filled
      const hasFiles = formData.creatives && formData.creatives.length > 0;
      const hasDescription = formData.new_creative_description && formData.new_creative_description.trim();
      
      // Check if both are filled
      if (hasFiles && hasDescription) {
        alert('Please choose only one option: either upload creative files OR provide a description. You cannot fill both.');
        return false;
      }
      
      if (!hasFiles && !hasDescription) {
        alert('Please either upload at least one creative file OR provide a description for the new creative');
        return false;
      }
    }

    if (formData.creative_type === 'existing' && !formData.existing_creative_description.trim()) {
      alert('Please describe the existing creative from the library');
      return false;
    }

    if (formData.creative_type === 'request' && !formData.new_creative_description.trim()) {
      alert('Please describe the creative you would like to have created');
      return false;
    }

    return true;
  };

  // Expose validate and getData methods to parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      return validateForm();
    },
    getData: () => {
      return formData;
    }
  }));

  const renderCreativeSourceFields = () => {
    switch (formData.creative_type) {
      case 'upload':
        return (
            <div className="space-y-4">
              <div className="space-y-2">
                <CreativeUpload
                  files={formData.creatives || []}
                  onFilesChange={handleFilesChange}
                  label="Upload Creative Files"
                  accept="image/*,video/*"
                  description="Upload your creative files (JPG, PNG, GIF, MP4, etc.)"
                  showPreviews={true}
                  multiple={true}
                  labelSuffix={""}
                  
                />
              </div>
              
              {/* OR Divider */}
              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-xs text-gray-400 font-medium">OR</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">
                  New Creative Description
                </Label>
              <Textarea
                  placeholder="Describe the creative you would like to have created..."
                  value={formData.new_creative_description}
                  onChange={(e) => handleInputChange('new_creative_description', e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>
          </div>
        );

      case 'existing':
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Existing Creative Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              placeholder="Describe the existing creative from Amplify library..."
              value={formData.existing_creative_description}
              onChange={(e) => handleInputChange('existing_creative_description', e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column - Creative Assets Form */}
        <div className="col-span-2">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Image className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Creative Assets</h2>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Creative Source */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Creative Source <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => handleCreativeTypeChange('existing')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${formData.creative_type === 'existing'
                          ? 'border-yellow-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 ${formData.creative_type === 'existing'
                            ? 'border-yellow-600 bg-yellow-600'
                            : 'border-gray-300'
                          }`}>
                          {formData.creative_type === 'existing' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                        <span className="text-sm font-medium">Existing Library Asset</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleCreativeTypeChange('upload')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${formData.creative_type === 'upload'
                          ? 'border-yellow-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 ${formData.creative_type === 'upload'
                            ? 'border-yellow-600 bg-yellow-600'
                            : 'border-gray-300'
                          }`}>
                          {formData.creative_type === 'upload' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                        <span className="text-sm font-medium">New Creative</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Creative Source Specific Fields */}
                {renderCreativeSourceFields()}

                {/* Ad Headline */}
                <div className="space-y-2">
                  <Label htmlFor="adHeadline" className="text-sm font-medium text-gray-700">
                    Ad Headline <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="adHeadline"
                    placeholder="e.g., Experience the Sunset"
                    value={formData.ad_headline || ''}
                    onChange={(e) => handleInputChange('ad_headline', e.target.value)}
                    maxLength={50}
                  />
                </div>

                {/* Body Copy */}
                <div className="space-y-2">
                  <Label htmlFor="bodyCopy" className="text-sm font-medium text-gray-700">
                    Body Copy <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="bodyCopy"
                    placeholder="Join us for an unforgettable evening with Corona. Limited time offer at participating locations."
                    value={formData.body_copy || ''}
                    onChange={(e) => handleInputChange('body_copy', e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <div className='mx-auto gap-6 grid grid-cols-3'>

                  {/* Min Age */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Minimum Age
                    </Label>
                    <CustomDropdown
                      options={[
                        ...Array.from({ length: 47 }, (_, i) => ({
                          value: (i + 19).toString(),
                          label: (i + 19).toString()
                        })),
                        { value: '65+', label: '65+' }
                      ]}
                      value={formData.min_age}
                      onChange={(value) => handleInputChange('min_age', value)}
                      placeholder="Select minimum age"
                    />
                  </div>

                  {/* Max Age */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Maximum Age
                    </Label>
                    <CustomDropdown
                      options={[
                        ...Array.from({ length: 47 }, (_, i) => ({
                          value: (i + 19).toString(),
                          label: (i + 19).toString()
                        })),
                        { value: '65+', label: '65+' }
                      ]}
                      value={formData.max_age}
                      onChange={(value) => handleInputChange('max_age', value)}
                      placeholder="Select maximum age"
                    />
                  </div>

                  {/* CTA Type */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Call to Action Type
                    </Label>
                    <CustomDropdown
                      options={campaignOptions?.cta_type || []}
                      value={formData.cta_type}
                      onChange={(value) => handleInputChange('cta_type', value)}
                      placeholder="Select CTA type"
                    />
                  </div>
                </div>

                {/* Additional Comments */}
                <div className="space-y-2">
                  <Label htmlFor="additionalComment" className="text-sm font-medium text-gray-700">
                    Additional Comments
                  </Label>
                  <Textarea
                    id="additionalComment"
                    placeholder="Any other considerations for the ad” in creative upload section.."
                    value={formData.additional_comment || ''}
                    onChange={(e) => handleInputChange('additional_comment', e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Live Ad Preview */}
        <div className="col-span-1">
          <LiveAdPreview
            campaignName={campaignName}
            adHeadline={formData.ad_headline}
            bodyCopy={formData.body_copy}
            ctaType={formData.cta_type}
            creativeFiles={formData.creatives}
          />
        </div>
      </div>
    </div>
  );
});

export default CreativeUploadForm;
